<template>
    <div class="tw-w-full sm:tw-max-w-[350px]">
        <div class="tw-w-full">
            <div
                v-if="!haveEligiblePackages && props.disableBuyPackage"
                class="tw-w-full"
            >
                <v-btn
                    color="primary"
                    class="tw-w-full"
                    :to="`/${studioRoute}/packages`"
                    >{{ $t('nav.buyPackages') }}</v-btn
                >
            </div>
            <template v-else>
                <v-select
                    :label="$t('message.choosePackage')"
                    :items="allPkgs"
                    item-text="name"
                    item-value="id"
                    return-object
                    v-model="selectedPkg"
                    required
                    attach
                    @input="onSelectPackage"
                    solo
                    outlined
                    flat
                    hide-details
                    class="tw-w-full"
                >
                    <template v-slot:item="data">
                        <div class="tw-flex tw-flex-col tw-py-2">
                            <div class="custom-selection">
                                {{ parsePackageName(data.item) }}
                            </div>
                            <RenderDropdownHint
                                :items="getDropdownHint(data)"
                            ></RenderDropdownHint>
                        </div>
                    </template>
                    <template v-slot:selection="data">
                        <div>
                            <div class="custom-selection tw-py-2">
                                {{ parsePackageName(data.item) }}
                            </div>
                            <RenderDropdownHint
                                :items="getDropdownHint(data)"
                            ></RenderDropdownHint>
                        </div>
                    </template>
                </v-select>
                <p v-if="packageHint && !props.hideHint">
                    {{ packageHint }}
                </p>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    computed,
    h,
    onMounted,
    watch,
    ref,
    type Ref,
    defineComponent,
} from 'vue'
import { useRoute } from '@/util/vueLegacyWrapper.js'
import { dateStringFromTimestamp } from '@/util/dateformat.js'
import { useStudioStore } from './stores/studioStore.js'
import { useI18n } from 'vue-i18n-composable'
import type { BookingType, Package, StudioClass } from './types/types.js'
import _ from 'lodash'
import { storeToRefs } from 'pinia'

const props = defineProps<{
    hideHint?: boolean
    allMemberships: Package[]
    myMemberships: Package[]
    selectedUserPkg: Package | undefined
    studioClass: StudioClass
    dropdownHintType?: BookingType
    disableBuyPackage?: boolean
}>()

const route = useRoute()
const studioRoute = route.params.studioRoute

const { t: $t } = useI18n()
const allPkgs = computed(() => {
    const myPkgs = [
        { header: $t('message.myPackages') },
        ...props.myMemberships,
    ]

    return [...myPkgs, ...props.allMemberships]
})
const studioStore = useStudioStore()
const { studio } = storeToRefs(studioStore)

const selectedPkg = ref([]) as Ref<Package | undefined>

const haveEligiblePackages = computed(() => {
    return props.myMemberships.length > 0
})
const getBookingInfoNodes = (membership: any) => {
    return h('span', { class: 'tw-text-success' }, [
        `Allows booking up to ${membership.numGroupBookable} spots`,
    ])
}
const getExpiryDateNode = (membership: any) => {
    return h(
        'span',
        { class: 'tw-text-textDisabled' },
        getFormattedExpiryDate(membership)
    )
}

const getSeparatorNode = () =>
    h('span', { class: 'tw-mx-1 tw-hidden sm:tw-block' }, ' | ')

onMounted(() => {
    autoSelectPackage()
})

const getDropdownHint = (data: any) => {
    const hints = []

    if (!data.item.userId) {
        return []
    }
    if (props.dropdownHintType === 'group') {
        hints.push(getBookingInfoNodes(data.item))
        hints.push(getSeparatorNode())
    }

    hints.push(getExpiryDateNode(data.item))
    return hints
}
// Create a custom render component
const RenderDropdownHint = defineComponent({
    props: {
        items: {
            type: Array,
            required: true,
        },
    },
    setup(contentProps: any) {
        return () => {
            return h(
                'div',
                {
                    class: 'tw-flex tw-flex-col sm:tw-flex-row tw-text-xs',
                },
                contentProps.items
            )
        }
    },
})
watch(
    () => props.selectedUserPkg,
    (newValue) => {
        selectedPkg.value = newValue
    }
)
function autoSelectPackage() {
    const skip =
        // no packages
        !props.myMemberships ||
        props.myMemberships.length === 0 ||
        props.myMemberships.length > 1
    if (skip) {
        emitInput('onSelectPackage', null, true)
        return
    }

    const pkg = props.myMemberships[0]
    selectedPkg.value = pkg
    onSelectPackage(pkg)
}

const getFormattedExpiryDate = (membership: any) => {
    const dateStr = dateStringFromTimestamp(
        membership.expiresOn,
        studio.value.timezone,
        'D MMM YYYY'
    )
    return `Expires on ${dateStr}`
}

function parsePackageName(membership: any) {
    const { name, expiresOn, classesLeft, numberClasses } = membership
    if (expiresOn && classesLeft !== undefined && numberClasses !== undefined) {
        // in case classesLeft or numberClasses is 0
        const dateStr = dateStringFromTimestamp(
            expiresOn,
            studio.value.timezone,
            undefined
        )
        return $t('message.packageExpiryWithCount', {
            name: name,
            dateStr: dateStr,
            classesLeft: classesLeft,
            numberClasses: numberClasses,
        })
    }
    return name
}

function onSelectPackage(pkg: Package) {
    const isSelectedMine = isMyPackage(pkg)
    emitInput('onSelectPackage', pkg, isSelectedMine)
}

const packageHint = computed(() => {
    const packageItem = selectedPkg.value
    if (!packageItem) {
        return
    }

    if (isMyPackage(packageItem)) {
        return
    }
    const currency = studio.value.currency ?? '$'
    return $t('message.packagePurchaseMessage', {
        price: packageItem.price,
        currency: currency,
    })
})

function isMyPackage(packageItem: any) {
    if (!props.myMemberships) {
        return false
    }

    const foundIdx = props.myMemberships.findIndex((p: any) => {
        if (!p) return false
        if (!p.normalPackageId) {
            return p.name === packageItem.name
        }

        return p.normalPackageId === packageItem.normalPackageId
    })
    return foundIdx !== -1
}

const emitInput = defineEmits<{
    (e: 'onSelectPackage', pkg: Package | null, isMyPackage: boolean): void
}>()
</script>
